<template>
  <div
    class="service-product-selector"
    :class="{ 'service-product-selector-fixed': fixedProductInputWidth }"
  >
    <div :class="selectorRowClass">
      <div :class="productInputColumnClass">
        <label class="label">Product Name / Product Code</label>
        <el-autocomplete
          v-model="displayValue"
          class="w-100"
          :fetch-suggestions="querySearch"
          placeholder="ค้นหาชื่อหรือรหัสสินค้า"
          :trigger-on-focus="false"
          :disabled="disabled"
          value-key="display"
          clearable
          @input="handleInput"
          @select="handleSelect"
          @clear="handleClear"
        >
          <template slot-scope="{ item }">
            <div
              v-if="item.isNoData"
              style="cursor: not-allowed"
              @click.stop.prevent
            >
              <div
                style="
                  padding: 8px 0;
                  color: #999;
                  text-align: center;
                  font-size: 0.85rem;
                "
              >
                {{ item.display }}
              </div>
            </div>
            <div
              v-else
              class="suggestion-item"
              :style="getItemRenderData(item).suggestionStyle"
              @click="onItemClick(item, $event)"
            >
              <div
                class="product-info"
                :style="getItemRenderData(item).productInfoStyle"
              >
                <span class="name" style="font-weight: 500">{{
                  item.name
                }}</span>
                <span class="code" style="font-size: 0.85rem; color: #666"
                  >({{ getProductCode(item) }})</span
                >
              </div>
              <span
                class="stock-badge"
                :style="getItemRenderData(item).badgeStyle"
              >
                {{ getItemRenderData(item).label }}
              </span>
            </div>
          </template>
        </el-autocomplete>
      </div>
      <div v-if="productQuantity !== null" :class="quantityColumnClass">
        <label class="label text-nowrap">จำนวนคงเหลือ</label>
        <el-input v-model="productQuantity" disabled type="number"></el-input>
      </div>
    </div>
  </div>
</template>

<script>
import { Input, Autocomplete } from "element-ui";
import { backendAdminClient } from "@/dependencies";
import { getServiceProductMeta } from "@/lib/mapper";
import { isNil } from "ramda";

function getFirstNonNilValue(...values) {
  const foundValue = values.find((value) => !isNil(value));

  return isNil(foundValue) ? null : foundValue;
}

export default {
  name: "ServiceProductSelector",
  components: {
    "el-input": Input,
    "el-autocomplete": Autocomplete,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    fixedProductInputWidth: {
      type: Boolean,
      default: false,
    },
    disabledProductIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      displayValue: "",
      productQuantity: null,
      selectedProduct: null,
      shouldKeepInputOnClear: false,
      latestSearchRequestId: 0,
    };
  },
  computed: {
    selectorRowClass() {
      return this.fixedProductInputWidth
        ? "service-product-selector-row-fixed"
        : "row";
    },
    productInputColumnClass() {
      if (!this.fixedProductInputWidth) return "col";

      return this.productQuantity !== null
        ? "product-input-fixed"
        : "product-input-full";
    },
    quantityColumnClass() {
      return this.fixedProductInputWidth
        ? "product-quantity-fixed"
        : "col-auto quantity-col";
    },
    normalizedDisabledProductIds() {
      return this.disabledProductIds.map((id) => String(id));
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(product) {
        if (product && product.id) {
          this.selectedProduct = product;
          this.displayValue = this.getProductDisplay(product);
          this.productQuantity = this.getProductStock(product);
        } else {
          this.selectedProduct = null;
          if (this.shouldKeepInputOnClear) {
            this.shouldKeepInputOnClear = false;
          } else {
            this.displayValue = "";
          }
          this.productQuantity = null;
        }
      },
    },
  },
  methods: {
    async querySearch(queryString, cb) {
      const textSearch = queryString ? queryString.trim() : "";

      this.latestSearchRequestId += 1;
      const requestId = this.latestSearchRequestId;

      if (!textSearch || textSearch.length < 2) {
        cb([]);

        return;
      }

      try {
        const results =
          await backendAdminClient.subscriptions.searchServiceProducts(
            textSearch
          );

        if (requestId !== this.latestSearchRequestId) {
          return;
        }

        if (results.length === 0) {
          cb([
            { display: "ไม่มีสินค้าที่ค้นหา", disabled: true, isNoData: true },
          ]);

          return;
        }

        cb(results);
      } catch (error) {
        console.error("Failed to fetch service product suggestions:", error);

        if (requestId === this.latestSearchRequestId) {
          cb([]);
        }
      }
    },
    isItemSelectable(item) {
      if (item.isNoData) return false;
      if (this.isProductAlreadySelected(item)) return false;
      const meta = this.getProductRenderMeta(item);
      return meta.selectable;
    },
    isProductAlreadySelected(product) {
      if (!product || isNil(product.id)) return false;

      return this.normalizedDisabledProductIds.includes(String(product.id));
    },
    getProductDisplay(product) {
      return product.display || `${product.name} (${this.getProductCode(product)})`;
    },
    getProductCode(product) {
      return product.code || product.product_code || product.productCode || "";
    },
    getProductStock(product) {
      return getFirstNonNilValue(product.stock, product.quantity);
    },
    getProductCost(product) {
      return getFirstNonNilValue(product.cost, product.cost_price, product.costPrice);
    },
    handleInput(value) {
      if (
        !this.selectedProduct ||
        value === this.getProductDisplay(this.selectedProduct)
      ) {
        return;
      }

      this.selectedProduct = null;
      this.productQuantity = null;
      this.shouldKeepInputOnClear = true;
      this.$emit("input", null);
    },
    onItemClick(item, event) {
      if (!this.isItemSelectable(item)) {
        event.stopPropagation();
      }
    },
    handleSelect(item) {
      if (!this.isItemSelectable(item)) {
        this.displayValue = this.selectedProduct
          ? this.getProductDisplay(this.selectedProduct)
          : "";
        return;
      }

      this.selectedProduct = item;
      this.displayValue = item.display;
      this.productQuantity = this.getProductStock(item);
      this.emitProduct();
    },
    handleClear() {
      this.displayValue = "";
      this.selectedProduct = null;
      this.productQuantity = null;

      this.emitProduct();
    },
    emitProduct() {
      if (this.selectedProduct) {
        this.$emit("input", {
          id: this.selectedProduct.id,
          name: this.selectedProduct.name,
          code: this.getProductCode(this.selectedProduct),
          stock: this.getProductStock(this.selectedProduct),
          cost: this.getProductCost(this.selectedProduct),
          status: this.selectedProduct.status || "",
          promotion_price: getFirstNonNilValue(
            this.selectedProduct.promotion_price,
            this.selectedProduct.promotionPrice
          ),
          promotion_started_at: getFirstNonNilValue(
            this.selectedProduct.promotion_started_at,
            this.selectedProduct.promotionStartedAt
          ),
          promotion_ended_at: getFirstNonNilValue(
            this.selectedProduct.promotion_ended_at,
            this.selectedProduct.promotionEndedAt
          ),
        });
      } else {
        this.$emit("input", null);
      }
    },
    getItemRenderData(item) {
      const meta = this.getProductRenderMeta(item);
      return {
        suggestionStyle: {
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "4px 0",
          ...(!meta.selectable && { cursor: "not-allowed" }),
        },
        productInfoStyle: {
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "6px",
          flex: "1",
          minWidth: "0",
          ...(meta.opacity < 1 && { opacity: String(meta.opacity) }),
        },
        badgeStyle: {
          display: "inline-flex",
          alignItems: "center",
          padding: "2px 10px",
          borderRadius: "4px",
          border: "1px solid",
          fontSize: "0.7rem",
          fontWeight: "600",
          textTransform: "uppercase",
          letterSpacing: "0.3px",
          whiteSpace: "nowrap",
          lineHeight: "1.3",
          flexShrink: "0",
          ...meta.badgeColor,
        },
        label: meta.label,
      };
    },
    getProductRenderMeta(item) {
      if (this.isProductAlreadySelected(item)) {
        return {
          label: "เลือกแล้ว",
          badgeColor: {
            background: "#e9ecef",
            color: "#495057",
            borderColor: "#ced4da",
          },
          selectable: false,
          opacity: 0.4,
          selected: true,
        };
      }

      return getServiceProductMeta(item.status);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.service-product-selector {
  width: 100%;

  .label {
    margin-bottom: 5px;
    display: block;
    line-height: 1.6;
    font-size: 1rem;
    font-weight: bold;
  }

  .w-100 {
    width: 100%;
  }

  .text-nowrap {
    white-space: nowrap;
  }

  .quantity-col {
    max-width: 140px;
    min-width: 120px;
  }

  .service-product-selector-row-fixed {
    display: flex;
    flex-wrap: nowrap;
    column-gap: 30px;
    width: 100%;
  }

  .product-input-full {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
  }

  .product-input-fixed {
    flex: 1 1 auto;
    max-width: none;
    min-width: 0;
  }

  .product-quantity-fixed {
    flex: 0 0 20%;
    max-width: 20%;
    min-width: 140px;
  }
}
</style>
